import { useState, useCallback } from 'react';
import actionWebInvoke from '../utils';
import { uploadAssetsInBatches } from '../lib/uploadAssets';

const TEST_LOGO_URL = 'https://upload.wikimedia.org/wikipedia/commons/c/c5/Ikea_logo.svg';
const HEADER_PATTERN = /header/i;

export default function useDevHandlers({
  ue,
  texts,
  collectPageComponents,
  collectTextEntry,
  getEditorContext,
  fetchDamEntities,
  actionUrls,
}) {
  const { TEST_LLM_ACTION_URL, FETCH_NODE_JSON_ACTION_URL, UPLOAD_ACTION_URL, REPLACE_PAGE_JCR_ACTION_URL } = actionUrls;

  const [showDevTab, setShowDevTab] = useState(false);
  const [testLlmStatus, setTestLlmStatus] = useState({ phase: 'idle', result: '' });
  const [testDamStatus, setTestDamStatus] = useState({ phase: 'idle', result: '' });
  const [testLogoStatus, setTestLogoStatus] = useState({ phase: 'idle', result: '' });
  const [testLogoMatchStatus, setTestLogoMatchStatus] = useState({ phase: 'idle', result: '' });
  const [pageDump, setPageDump] = useState({ phase: 'idle', json: '' });

  const handleTestLlm = useCallback(async () => {
    if (!TEST_LLM_ACTION_URL) {
      setTestLlmStatus({ phase: 'error', result: 'Test LLM action not deployed yet' });
      return;
    }
    setTestLlmStatus({ phase: 'testing', result: '' });
    try {
      const resp = await actionWebInvoke(
        TEST_LLM_ACTION_URL,
        ue.imsToken ? { Authorization: `Bearer ${ue.imsToken}` } : {},
        {},
        { method: 'POST' }
      );
      const body = resp?.body || resp || {};
      if (body.ok) {
        setTestLlmStatus({ phase: 'ok', result: `${body.text} (model: ${body.model})` });
      } else {
        setTestLlmStatus({ phase: 'error', result: body.error || 'Unexpected response' });
      }
    } catch (err) {
      setTestLlmStatus({ phase: 'error', result: err?.message || 'Test failed' });
    }
    setTimeout(() => setTestLlmStatus((s) => (s.phase !== 'testing' ? { phase: 'idle', result: '' } : s)), 8000);
  }, [ue.imsToken]);

  const handleTestDam = useCallback(async () => {
    if (!FETCH_NODE_JSON_ACTION_URL) {
      setTestDamStatus({ phase: 'error', result: 'fetch-node-json action URL not configured.' });
      return;
    }
    if (!ue.damFolderPath) {
      setTestDamStatus({ phase: 'error', result: `damFolderPath is empty.\nsiteSlug="${ue.siteSlug || ''}"\nThe UE context may not have resolved the site slug yet.` });
      return;
    }
    if (!ue.imsToken) {
      setTestDamStatus({ phase: 'error', result: 'No IMS token available.' });
      return;
    }

    setTestDamStatus({ phase: 'testing', result: '' });
    const slug = (ue.damFolderPath || '').replace(/^\/content\/dam\//, '');
    const fullUrl = `${ue.authorUrl}/api/assets/${slug}.json`;

    let result;
    try {
      result = await fetchDamEntities(ue);
    } catch (err) {
      setTestDamStatus({ phase: 'error', result: `Fetch failed: ${err?.message || String(err)}\nURL: ${fullUrl}` });
      return;
    }
    if (result.resp?.error) {
      const errBody = result.resp.error?.body || result.resp.error;
      setTestDamStatus({ phase: 'error', result: `Action error: ${typeof errBody === 'object' ? JSON.stringify(errBody) : errBody}` });
      return;
    }

    const { assets, folders, allEntities, total: srTotal } = result;
    if (assets.length > 0) {
      const summary = assets.slice(0, 8).map((e) => {
        const name = e.properties?.name || e.properties?.['repo:name'] || '?';
        const mime = e.properties?.['dc:format'] || e.properties?.contentType || '(no mime)';
        return `  ${name}  ${mime}  [${(e.class || []).join(', ')}]`;
      }).join('\n');
      setTestDamStatus({
        phase: 'ok',
        result: `${assets.length} assets shown (API total: ${srTotal}) — ${folders.length} folders\nURL: ${fullUrl}\n${summary}${assets.length > 8 ? `\n  … +${assets.length - 8} more on this page` : ''}`,
      });
    } else {
      setTestDamStatus({
        phase: 'error',
        result: `0 assets (${folders.length} folders, ${allEntities.length} entities)\nURL: ${fullUrl}\nFirst entity: ${JSON.stringify(allEntities[0] || {}).substring(0, 400)}`,
      });
    }
    setTimeout(() => setTestDamStatus((s) => (s.phase !== 'testing' ? { phase: 'idle', result: '' } : s)), 30000);
  }, [ue.imsToken, ue.authorUrl, ue.orgId, ue.damFolderPath, ue.siteSlug]);

  const handleTestLogoUpload = useCallback(async () => {
    if (!UPLOAD_ACTION_URL) { setTestLogoStatus({ phase: 'error', result: 'Upload action not configured' }); return; }
    if (!ue.imsToken || !ue.authorUrl || !ue.damFolderPath) {
      setTestLogoStatus({ phase: 'error', result: 'UE context incomplete (token/author/dam)' }); return;
    }
    setTestLogoStatus({ phase: 'uploading', result: '' });
    try {
      const items = [{ url: TEST_LOGO_URL, name: 'LOGO' }];
      let result = null;
      for await (const r of uploadAssetsInBatches(items, {
        actionUrl: UPLOAD_ACTION_URL, imsToken: ue.imsToken,
        authorUrl: ue.authorUrl, orgId: ue.orgId, targetFolderPath: ue.damFolderPath,
      })) { result = r; }
      if (result?.ok) {
        setTestLogoStatus({ phase: 'ok', result: `Uploaded as: ${result.fileName}\nPath: ${result.path}` });
      } else {
        setTestLogoStatus({ phase: 'error', result: result?.error || 'Upload failed' });
      }
    } catch (err) {
      setTestLogoStatus({ phase: 'error', result: err?.message || 'Upload failed' });
    }
    setTimeout(() => setTestLogoStatus((s) => s.phase !== 'uploading' ? { phase: 'idle', result: '' } : s), 15000);
  }, [ue.imsToken, ue.authorUrl, ue.orgId, ue.damFolderPath]);

  const handleTestLogoMatch = useCallback(async () => {
    setTestLogoMatchStatus({ phase: 'testing', result: '' });
    const lines = [];
    try {
      const { assets } = await fetchDamEntities(ue);
      const allNames = assets.map((e) => e.properties?.name || e.properties?.['repo:name'] || '?');
      const logoEntity = assets.find((e) => {
        const name = e.properties?.name || e.properties?.['repo:name'] || '';
        return /^LOGO(\.|$)/i.test(name);
      });
      lines.push(`DAM: ${assets.length} assets`);
      lines.push(`All filenames: ${allNames.join(', ')}`);
      lines.push(logoEntity
        ? `LOGO found: "${logoEntity.properties?.name || logoEntity.properties?.['repo:name']}"`
        : 'LOGO NOT FOUND in DAM');

      let ctx;
      try { ctx = await getEditorContext(ue); } catch (_) { /* fall through */ }
      if (!ctx) { lines.push('ERROR: editorActions not available'); }
      else {
        const { components } = await collectPageComponents(ctx.editorActions, ctx.editorState);
        const withImage = components.filter((c) => c.imagePropertyName);
        lines.push(`\nPage: ${components.length} components, ${withImage.length} with images`);

        const LOGO_PATH_PATTERN = /logo/i;
        let logoTargetId = null;
        const firstImageComp = components.find((c) => c.imagePropertyName);
        for (const c of withImage) {
          if (HEADER_PATTERN.test(c.component || '') || HEADER_PATTERN.test(c.parent || '')) {
            logoTargetId = c.id;
            lines.push(`Detection: label match on "${c.component}" / "${c.parent}"`);
            break;
          }
          if (LOGO_PATH_PATTERN.test(c.currentImage || '')) {
            logoTargetId = c.id;
            lines.push(`Detection: image path contains "logo" → "${c.currentImage}"`);
            break;
          }
        }
        if (!logoTargetId && firstImageComp) {
          logoTargetId = firstImageComp.id;
          lines.push(`Detection: first-image fallback → "${firstImageComp.id}"`);
        }
        if (!logoTargetId) lines.push('Detection: NO image component found');

        for (const c of withImage) {
          const tag = c.id === logoTargetId ? '→ LOGO' : '  (LLM)';
          lines.push(`${tag}  "${c.id.substring(0, 8)}…"  component="${c.component}"  parent="${c.parent}"  img="${c.currentImage || '(none)'}"`);
        }
      }

      setTestLogoMatchStatus({ phase: 'ok', result: lines.join('\n') });
    } catch (err) {
      lines.push(`ERROR: ${err?.message || err}`);
      setTestLogoMatchStatus({ phase: 'error', result: lines.join('\n') });
    }
    setTimeout(() => setTestLogoMatchStatus((s) => s.phase !== 'testing' ? { phase: 'idle', result: '' } : s), 30000);
  }, [ue.conn, ue.imsToken, ue.authorUrl, ue.orgId, ue.damFolderPath]);

  const handleDumpPage = useCallback(async () => {
    let ctx;
    try { ctx = await getEditorContext(ue); } catch (_) { /* fall through */ }
    if (!ctx) { setPageDump({ phase: 'error', json: 'editorActions not available' }); return; }
    const { editorActions, editables } = ctx;
    setPageDump({ phase: 'collecting', json: '' });
    try {
      const pageTexts = [];
      for (let i = 0; i < editables.length; i++) {
        const ed = editables[i];
        if (!ed || !ed.resource) continue;
        try {
          const details = await editorActions.details(ed);
          const entry = collectTextEntry(details, ed, i, editables);
          if (entry) pageTexts.push(entry);
        } catch (_) { /* skip */ }
      }
      setPageDump({ phase: 'done', json: JSON.stringify({ pageTexts, scrapedTexts: texts }, null, 2) });
    } catch (err) {
      setPageDump({ phase: 'error', json: err?.message || 'Failed to read editor state' });
    }
  }, [ue.conn, texts]);

  const handleDumpAll = useCallback(async () => {
    let ctx;
    try { ctx = await getEditorContext(ue); } catch (_) { /* fall through */ }
    if (!ctx) { setPageDump({ phase: 'error', json: 'editorActions not available' }); return; }
    const { editorActions, editorState, editables } = ctx;
    setPageDump({ phase: 'collecting', json: '' });
    try {
      const entries = [];
      for (const ed of editables) {
        if (!ed || !ed.resource) continue;
        try {
          const details = await editorActions.details(ed);
          entries.push({ editable: ed, details });
        } catch (_) {
          entries.push({ editable: ed, details: null, error: 'details() failed' });
        }
      }
      const dump = {
        timestamp: new Date().toISOString(),
        editablesCount: editables.length,
        detailsCollected: entries.length,
        editorState,
        entries,
      };
      setPageDump({ phase: 'done', json: JSON.stringify(dump, null, 2) });
    } catch (err) {
      setPageDump({ phase: 'error', json: err?.message || 'Failed to read editor state' });
    }
  }, [ue.conn]);

  const handleDumpComponents = useCallback(async () => {
    let ctx;
    try { ctx = await getEditorContext(ue); } catch (_) { /* fall through */ }
    if (!ctx) { setPageDump({ phase: 'error', json: 'editorActions not available' }); return; }
    setPageDump({ phase: 'collecting', json: '' });
    try {
      const { components } = await collectPageComponents(ctx.editorActions, ctx.editorState);

      let damListing = [];
      let damError = '';
      if (!FETCH_NODE_JSON_ACTION_URL) {
        damError = 'fetch-node-json action URL not configured';
      } else if (!ue.damFolderPath) {
        damError = `damFolderPath is empty (siteSlug="${ue.siteSlug}")`;
      } else {
        try {
          const { assets } = await fetchDamEntities(ue);
          damListing = assets.map((e) => ({
            name: e.properties?.name || e.properties?.['repo:name'] || '',
            mimeType: e.properties?.['dc:format'] || e.properties?.contentType || '',
            title: e.properties?.['dc:title'] || '',
            class: (e.class || []).join(', '),
          }));
        } catch (err) {
          damError = err?.message || 'DAM listing call failed';
        }
      }

      const dump = { pageComponents: components, damListing, damError: damError || undefined, scrapedTexts: texts };
      setPageDump({ phase: 'done', json: JSON.stringify(dump, null, 2) });
    } catch (err) {
      setPageDump({ phase: 'error', json: err?.message || 'Failed' });
    }
  }, [ue.conn, ue.imsToken, ue.authorUrl, ue.orgId, ue.damFolderPath, texts, collectPageComponents]);

  const [sitePages, setSitePages] = useState({ phase: 'idle', result: '' });

  const handleListPages = useCallback(async () => {
    if (!FETCH_NODE_JSON_ACTION_URL) { setSitePages({ phase: 'error', result: 'fetch-node-json not configured' }); return; }
    if (!ue.pagesRoot || !ue.imsToken || !ue.authorUrl) {
      setSitePages({ phase: 'error', result: `Missing context: pagesRoot="${ue.pagesRoot || ''}", hasToken=${!!ue.imsToken}` }); return;
    }
    setSitePages({ phase: 'loading', result: '' });
    try {
      const resource = ue.pagesRoot;
      const resp = await actionWebInvoke(
        FETCH_NODE_JSON_ACTION_URL,
        { Authorization: `Bearer ${ue.imsToken}` },
        { authorUrl: ue.authorUrl, orgId: ue.orgId, bearer: ue.imsToken, resource: `${resource}.3`, queryString: '' },
        { method: 'POST' }
      );
      const json = resp?.body || resp || {};
      const pages = [];
      function walk(node, parentPath) {
        for (const [key, val] of Object.entries(node)) {
          if (!val || typeof val !== 'object' || val['jcr:primaryType'] !== 'cq:Page') continue;
          const jcrContent = val['jcr:content'] || {};
          const title = jcrContent['jcr:title'] || key;
          const resType = jcrContent['sling:resourceType'] || '';
          const hidden = jcrContent.hideInNav === true || jcrContent.hideInNav === 'true';
          const pagePath = `${parentPath}/${key}`;
          pages.push({ name: key, path: pagePath, title, resType, hidden });
          walk(val, pagePath);
        }
      }
      walk(json, resource);
      pages.sort((a, b) => a.path.localeCompare(b.path));
      const lines = [`${pages.length} pages under ${resource}`, ''];
      for (const p of pages) {
        const depth = (p.path.split('/').length - resource.split('/').length);
        const indent = depth > 1 ? '  '.repeat(depth - 1) : '';
        const flags = [p.hidden ? 'hidden' : ''].filter(Boolean).join(' ');
        lines.push(`${indent}${p.name}  →  "${p.title}"${flags ? `  [${flags}]` : ''}`);
      }
      setSitePages({ phase: 'ok', result: lines.join('\n') });
    } catch (err) {
      setSitePages({ phase: 'error', result: err?.message || 'Failed to list pages' });
    }
    setTimeout(() => setSitePages((s) => s.phase !== 'loading' ? { phase: 'idle', result: '' } : s), 60000);
  }, [ue.imsToken, ue.authorUrl, ue.orgId, ue.pagesRoot]);

  const [replaceJcrStatus, setReplaceJcrStatus] = useState({ phase: 'idle', result: '' });

  const handleReplacePageJcr = useCallback(async () => {
    if (!REPLACE_PAGE_JCR_ACTION_URL) {
      setReplaceJcrStatus({ phase: 'error', result: 'replace-page-jcr action not deployed' }); return;
    }
    if (!ue.imsToken || !ue.authorUrl) {
      setReplaceJcrStatus({ phase: 'error', result: 'Missing UE context (token/authorUrl)' }); return;
    }

    const pagePath = ue.pagePath;
    if (!pagePath || !pagePath.startsWith('/content/')) {
      setReplaceJcrStatus({ phase: 'error', result: `Invalid pagePath: "${pagePath || ''}" — cannot determine current page` }); return;
    }

    setReplaceJcrStatus({ phase: 'running', result: `Replacing ${pagePath} via JCR…` });
    try {
      const resp = await actionWebInvoke(
        REPLACE_PAGE_JCR_ACTION_URL,
        { Authorization: `Bearer ${ue.imsToken}` },
        {
          pagePath,
          scrapedTexts: texts || {},
          damFolderPath: ue.damFolderPath || '',
          authorUrl: ue.authorUrl,
          orgId: ue.orgId,
          bearer: ue.imsToken,
        },
        { method: 'POST' }
      );
      const body = resp?.body || resp || {};
      if (body.error) {
        setReplaceJcrStatus({ phase: 'error', result: `Error: ${typeof body.error === 'object' ? JSON.stringify(body.error) : body.error}` });
      } else {
        const lines = [
          `Components found: ${body.components ?? '?'}`,
          `LLM replacements: ${body.llmReplacements ?? '?'}`,
          `Applied: ${body.applied ?? 0}`,
          `Failed: ${body.failed ?? 0}`,
        ];
        if (body.details) {
          const failDetails = body.details.filter((d) => !d.ok);
          if (failDetails.length > 0) {
            lines.push('', 'Failures:');
            for (const d of failDetails.slice(0, 5)) {
              lines.push(`  ${d.id}: ${d.error}`);
            }
          }
        }
        setReplaceJcrStatus({ phase: 'ok', result: lines.join('\n') });
      }
    } catch (err) {
      setReplaceJcrStatus({ phase: 'error', result: err?.message || 'Replace via JCR failed' });
    }
    setTimeout(() => setReplaceJcrStatus((s) => s.phase !== 'running' ? { phase: 'idle', result: '' } : s), 60000);
  }, [ue.imsToken, ue.authorUrl, ue.orgId, ue.damFolderPath, ue.pagePath, texts]);

  return {
    showDevTab, setShowDevTab,
    devTabProps: {
      onTestLlm: handleTestLlm,
      testLlmStatus,
      onTestDam: handleTestDam,
      testDamStatus,
      onTestLogoUpload: handleTestLogoUpload,
      testLogoStatus,
      onTestLogoMatch: handleTestLogoMatch,
      testLogoMatchStatus,
      onDumpPage: handleDumpPage,
      onDumpAll: handleDumpAll,
      onDumpComponents: handleDumpComponents,
      pageDump,
      onListPages: handleListPages,
      sitePages,
      onReplacePageJcr: handleReplacePageJcr,
      replaceJcrStatus,
    },
  };
}
